import { Fragment, useCallback, useEffect, useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Row,
  Col,
  Spin,
  Input,
  InputNumber,
  Button,
  Radio,
  Form,
  Checkbox,
  Empty,
  message,
  Tabs,
  DatePicker,
  Select,
  Dropdown,
  Space,
} from "antd";
import locale from 'antd/es/date-picker/locale/es_ES';
import Cookies from "universal-cookie";
import { LinkOutlined, DownOutlined } from '@ant-design/icons'
import moment from 'moment';

import { getBranchOffices } from "../../services/general";
import { useTranslation } from "react-i18next";
import config from '../../config';
import { normalizeDocumentNumber } from "../../utils/inputValidations";
import ReevaluationTable from "./components/ReevaluationTable";
import useDetailedReevaluation from "./hooks/useDetailedReevaluation";
import countrySettings from './countrySettings.json';

const { TabPane } = Tabs;

const { format } = require('rut.js');

const PatientReevaluationList = () => {
  const cookie = new Cookies();
  const branchOfficeId = parseInt(cookie.get('branch_office_id'));
  const countryCode = cookie.get('country_code');
  const [branchOptions, setBranchOptions] = useState([]);
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [filters, setFilters] = useState({
    branchOfficeId,
    allBranchOffices: true,
    page: 1,
  });
  const [loading, setLoading] = useState(false);

  const {
    inProgress: inProgressReevaluations,
    completedDocuments: completedDocumentsReevaluations,
    finalized: finalizedReevaluations,
    filters: advancedFilters,
    setFilters: setAdvancedFilters,
    sorting,
    loading: advancedLoading,
    pagination: advancedPagination,
    handlePageChange,
    handleOrderChange,
    setSectionLoading,
    fetchAllSections,
    fetchDetailedReevaluations: fetchPatientsReevaluationDetailed,
  } = useDetailedReevaluation({ countryCode });

  const { t } = useTranslation();
  const showOutsourcingColumn = !!countrySettings.reevaluationList.showOutsourcingColumn[countryCode];
  const showAgeColumn = !!countrySettings.reevaluationList.showAgeColumn[countryCode];
  const showExpirationColumn = !!countrySettings.reevaluationList.showExpirationColumn[countryCode];
  const showTypeColumn = !!countrySettings.reevaluationList.showTypeColumn[countryCode];
  const useProtocolLabel = !!countrySettings.reevaluationList.useProtocolLabel[countryCode];
  const showAgeFilter = !!countrySettings.reevaluationList.showAgeFilter[countryCode];
  const showBranchEvaluationColumn = !!countrySettings.reevaluationList.showBranchEvaluationColumn[countryCode];
  const showUploadHistoryColumn = !!countrySettings.reevaluationList.showUploadHistoryColumn[countryCode];
  const showExpiredFilter = !!countrySettings.reevaluationList.showExpiredFilter[countryCode];
  const showDocumentsCompletedFilter = !!countrySettings.reevaluationList.showDocumentsCompletedFilter[countryCode];
  const colWidths = countrySettings.reevaluationList.columnWidths;
  const hasExtraColumns = showOutsourcingColumn || showTypeColumn;

  const fetchBranchOffices = useCallback(async () => {
    try {
      setSectionLoading('branchOffices', true);
      const response = await getBranchOffices({
        byUser: 1,
      });

      if (response?.length) {
        setBranchOptions(response);
      }
    } catch (error) {
      console.error('Error en Fetch Branch Offices', error);
      message.error({
        content: t('reservations.reevaluationList.fetchBranchOfficesError'),
        style: {
          marginTop: '45vh',
        },
      });
    } finally {
      setSectionLoading('branchOffices', false);
    }
  }, []);

  useEffect(() => {
    fetchBranchOffices();
  }, [fetchBranchOffices]);


  useEffect(() => {
    form.setFieldsValue({ allBranchOffices: true })
  }, []);

  useEffect(() => {
    fetchAllSections(advancedFilters);
  }, []);

  const onFinish = async (values) => {
    let params = values;
    if (!params.allBranchOffices) {
      delete params.allBranchOffices;
      params = { ...params, branchOfficeId };
    }

    if (!params.query) {
      delete params.query;
    } else {
      params.query = normalizeDocumentNumber(params.query);
    }

    if (!params.totalDocuments) {
      delete params.totalDocuments;
    }

    const filteredParams = Object.fromEntries(
      Object.entries(params).filter(([_, values]) => values && values !== '')
    );

    if (
      filteredParams.query || filteredParams.allBranchOffices
      || filteredParams.totalDocuments || filteredParams.branchOfficeId
    ) {

    }
  };

  const removeFilters = () => {
    form.resetFields();
    onFinish({
      branchOfficeId,
    });
  };

  const onFinishSelectFilters = async (values) => {
  let params = { ...values, page: 1 };

  if (!params.query) {
    delete params.query;
  } else {
    params.query = normalizeDocumentNumber(params.query);
  }

  if (params.date_range) {
    params.start_date = moment(params.date_range[0]).format('YYYY-MM-DD');
    params.end_date = moment(params.date_range[1]).format('YYYY-MM-DD');
    delete params.date_range;
  }

  try {
    const filteredParams = Object.fromEntries(
      Object.entries(params).filter(([_, value]) => value !== undefined && value !== '' && value !== null)
    );
    setAdvancedFilters(filteredParams);
    fetchAllSections(filteredParams);
  } catch (error) {
    console.error(error);
    message.error({
      content: 'Error al aplicar filtros',
      style: { marginTop: '45vh' },
    });
  }
  };


  const removeSelectFilters = () => {
    form.resetFields();
    onFinishSelectFilters({ page: 1 });
  };

  const formatDate = (date) => {
    return moment(date).format('DD-MM-YYYY');
  };



  const customTableHeader = () =>
  (
    <Row className="table-header">
      <Col lg={4} xs={12} className="align-item-center-start p-10">
        <h3>{t('reservations.reevaluationList.firstNameCol')}</h3>
      </Col>
      <Col lg={3} xs={12} className="center-item p-10">
        <h3 className="text-center">{t('reservations.reevaluationList.documentNumberCol')}</h3>
      </Col>
      <Col lg={3} xs={12} className="center-item p-10">
        <h3 className="text-center">{t('reservations.reevaluationList.reservationNumberCol')}</h3>
      </Col>
      <Col lg={2} xs={12} className="center-item p-10">
        <h3 className="text-center">{t('reservations.reevaluationList.dateCol')}</h3>
      </Col>
      <Col lg={3} xs={12} className="center-item p-10">
        <h3 className="text-center">{t('reservations.reevaluationList.serviceCol')}</h3>
      </Col>
      <Col lg={3} xs={12} className="center-item p-10">
        <h3 className="text-center">{t('reservations.reevaluationList.serviceTypeCol')}</h3>
      </Col>
      <Col lg={3} xs={12} className="center-item p-10">
        <h3 className="text-center">{t('reservations.reevaluationList.documentsCol')}</h3>
      </Col>
      <Col lg={1} xs={12} className="center-item p-10" />
      <Col lg={2} xs={24} className="center-item p-10" />
    </Row>
  );

  const publicSites = {
    "CL": config.publicSiteUrlChile,
    "PE": config.publicSiteUrlPeru,
  }

  const customTableBody = (item, index) =>
  (
    <Row className="table-body mb-15" key={index}>
      <Col lg={4} xs={12} className="align-item-center p-10">
        <h3 className="hide-desktop text-center">{t('reservations.reevaluationList.firstNameCol')}</h3>
        <p className="mb-0">{item.full_name}</p>
      </Col>
      <Col lg={3} xs={12} className="center-item p-10">
        <h3 className="hide-desktop text-center">{t('reservations.reevaluationList.documentNumberCol')}</h3>
        <p className="mb-0">
          {
            item.document_type === 'rut' ? format(item.document_number) : item.document_number
          }
        </p>
      </Col>
      <Col lg={3} xs={12} className="center-item p-10">
        <h3 className="hide-desktop text-center">{t('reservations.reevaluationList.reservationNumberCol')}</h3>
        <p className="mb-0">{item.reservation_number}</p>
      </Col>
      <Col lg={2} xs={12} className="center-item p-10">
        <h3 className="hide-desktop text-center">{t('reservations.reevaluationList.dateCol')}</h3>
        <p className="mb-0">{formatDate(item.date)}</p>
      </Col>
      <Col lg={3} xs={12} className="center-item p-10">
        <h3 className="hide-desktop text-center">{t('reservations.reevaluationList.serviceCol')}</h3>
        <p className="mb-0">{item.prestation}</p>
      </Col>
      <Col lg={3} xs={12} className="center-item p-10">
        <h3 className="hide-desktop text-center">{t('reservations.reevaluationList.serviceTypeCol')}</h3>
        <p className="mb-0">{item.prestation_type}</p>
      </Col>
      <Col lg={3} xs={12} className="center-item p-10">
        <h3 className="hide-desktop text-center">{t('reservations.reevaluationList.documentsCol')}</h3>
        <Row justify="center" gutter={5}>
          <Col>
            <p
              className={`mb-0
                ${item.uploaded_documents === 0 && 'disabled'}
                ${item.uploaded_documents === item.total_documents ? 'success' : 'error'}
                `}
            >
              {
                item.uploaded_documents < item.total_documents ?
                  'Incompleto'
                  :
                  item.uploaded_documents === 0 ? 'No disponible' : 'Completo'
              }
            </p>
          </Col>
          <Col>
            <p
              className={`mb-0
                ${item.uploaded_documents === 0 && 'disabled'}
                ${item.uploaded_documents === item.total_documents ? 'success' : 'error'}
                `}
            >
              {item.uploaded_documents}/{item.total_documents}
            </p>
          </Col>
        </Row>
      </Col>
      <Col lg={1} xs={12} className="center-item flex p-10">
        <a
          rel="noopener noreferrer"
          target="_blank"
          href={`${publicSites[countryCode]}app/pasos-paciente/${item.code_id}`}
        >
          <LinkOutlined />
        </a>
      </Col>
      <Col lg={2} xs={24} className="center-item p-10">
        <Link
          to={`/reevaluaciones/${item.medical_consultation_details_id}/editar`}
        >
          <Button
            className="cmt-button secondary outline"
            type="primary"
            size="small"
          >
            Reevaluar
          </Button>
        </Link>
      </Col>
    </Row>
  );

  const BranchOfficeMultiSelect = ({ branchOffices, value = [], onChange }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');

    const filteredbranchOffices = useMemo(() => {
      if (!search) return branchOffices;
      return branchOffices.filter(branch =>
        branch.name.toLowerCase().includes(search.toLowerCase())
      );
    }, [branchOffices, search]);

    const handleSelectAll = (checked) => {
      if (checked) {
        onChange(filteredbranchOffices.map(branch => branch.id));
      } else {
        onChange([]);
      }
    };

    const handleCheck = (checkedValues) => {
      onChange(checkedValues);
    };

    const content = (
      <div onClick={(event) => event.stopPropagation()} className="custom-filter-dropdown">
        <Input placeholder={t('reservations.reevaluationList.searchBranchOfficePlaceholder')} value={search} onChange={(e) => setSearch(e.target.value)} size="small" className="mb-10 cmt-input" />
        <div
          style={{
            maxHeight: 200,
            overflowY: 'auto',
            paddingRight: 8,
            marginBottom: 10,
          }}
        >
          <Checkbox
            indeterminate={
              value.length > 0 &&
              filteredbranchOffices.some(branch => value.includes(branch.id)) &&
              !filteredbranchOffices.every(branch => value.includes(branch.id))
            }
            checked={
              filteredbranchOffices.length > 0 &&
              filteredbranchOffices.every(branch => value.includes(branch.id))
            }
            onChange={(e) => handleSelectAll(e.target.checked)}
            className="mb-5 cmt-checkbox select-all-checkbox"
          >
            Seleccionar todas
          </Checkbox>
          <Checkbox.Group value={value} onChange={handleCheck} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {filteredbranchOffices.map((branch) => (
              <Checkbox key={branch.id} value={branch.id} className="cmt-checkbox">
                {branch.name}
              </Checkbox>
            ))}
          </Checkbox.Group>
        </div>
        <Button
          type="link"
          onClick={() => {
            onChange([]);
            setSearch('');
          }}
          size="small"
          className="p-0"
        >
          Borrar selección
        </Button>
      </div>
    );

    const label = value.length === 0
      ? t('reservations.reevaluationList.selectBranchOffice')
      : value.length === branchOffices.length
        ? t('reservations.reevaluationList.allBranchOfficesTitle')
        : `${value.length} seleccionadas`;

    return (
      <div>
        {advancedLoading.branchOffices ? (
          <Spin className="cmt-spin mt-10" />
          ) :(
          <Dropdown
            overlay={content}
            open={open}
            onOpenChange={setOpen}
            trigger={['click']}
            placement="bottomLeft"
          >
            <Button className="cmt-input" style={{ width: '100%', textAlign: 'left' }}>
              {label} <DownOutlined style={{ float: 'right', marginTop: 4 }} />
            </Button>
          </Dropdown>
        )}
      </div>);
  };

  const renderSelectFilters = () => (
    <Col lg={24} xs={24} className="filter-wrapper">
      <Form form={form} onFinish={onFinishSelectFilters} size="large" layout="vertical">
        <Row justify="space-between" gutter={[0, 10]}>
          <Col lg={14} xs={24}>
            <Row gutter={15}>
              <Col xxl={11} lg={14} xs={24}>
                <Form.Item name="query">
                  <Input className="cmt-input" placeholder={t('reservations.reevaluationList.companyOrPatientOrReservationrQueryPlaceholder')} />
                </Form.Item>
              </Col>
              <Col>
                <Button className="cmt-button primary" type="primary" htmlType="submit" disabled={loading}>
                  <i className="icon-system-search mr-5" />
                  Buscar
                </Button>
              </Col>
              <Col>
                <Button
                  onClick={() => removeSelectFilters()}
                  className="cmt-button primary non-outline p-0"
                  type="primary"
                  disabled={loading}
                >
                  Borrar filtros
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
        <Row gutter={[20, 20]} className="mt-10">
          <Col lg={6} xs={24}>
            <Form.Item name="date_range" label="Fecha de evaluación">
              <DatePicker.RangePicker locale={locale} format="DD-MM-YYYY" inputReadOnly placeholder={['Desde', 'Hasta']} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col lg={5} xs={24}>
            <Form.Item name="branch_offices" label={t('reservations.reevaluationList.branchOfficeLabel')} valuePropName="value">
              <BranchOfficeMultiSelect branchOffices={branchOptions} />
            </Form.Item>
          </Col>
          <Col lg={4} xs={24}>
            <Form.Item name="presence_required" label={t('reservations.reevaluationList.branchEvaluationCol')}>
              <Select placeholder="Seleccionar" className="cmt-select">
                <Select.Option value="">Todos</Select.Option>
                <Select.Option value={true}>Sí</Select.Option>
                <Select.Option value={false}>No</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          {showExpiredFilter && (
            <Col lg={4} xs={24}>
              <Form.Item name="is_expired" label="Vencidas">
                <Select placeholder="Seleccionar" className="cmt-select">
                  <Select.Option value="">Todos</Select.Option>
                  <Select.Option value={true}>Sí</Select.Option>
                  <Select.Option value={false}>No</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          )}
          {showDocumentsCompletedFilter && (
            <Col lg={4} xs={24}>
              <Form.Item name="documents_completed" label="Documentos completos">
                <Select placeholder="Seleccionar" className="cmt-select">
                  <Select.Option value="">Todos</Select.Option>
                  <Select.Option value={true}>Sí</Select.Option>
                  <Select.Option value={false}>No</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          )}
          {showAgeFilter && (
          <Col lg={5} xs={24}>
            <Form.Item label={t('reservations.reevaluationList.ageCol')}>
              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item name="min_age" noStyle>
                    <InputNumber
                      className="cmt-input"
                      placeholder={t('reservations.reevaluationList.minAgePlaceholder')}
                      min={0}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="max_age" noStyle>
                    <InputNumber
                      className="cmt-input"
                      placeholder={t('reservations.reevaluationList.maxAgePlaceholder')}
                      min={0}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form.Item>
          </Col>
          )}
        </Row>
      </Form>
    </Col>
  );

  const inProgressColumns = useMemo(() => {
    const baseColumns = [
      {
        key: 'full_name',
        title: t('reservations.reevaluationList.firstNameCol'),
        lg: colWidths.inProgress.full_name[countryCode] || 3,
        xs: 12,
        bodyClassName: 'align-item-center p-10',
      },
      { key: 'document_number', title: t('reservations.reevaluationList.documentNumberCol'), lg: 2, xs: 12 },
      { key: 'reservation_number', title: t('reservations.reevaluationList.reservationNumberCol'), lg: colWidths.inProgress.reservation_number[countryCode] || 1, xs: 12 },
      { key: 'company', title: t('reservations.reevaluationList.companyCol'), lg: colWidths.inProgress.company[countryCode] || 3, xs: 12 },
    ];

    if (showOutsourcingColumn) {
      baseColumns.push(
        { key: 'outsourcing_company_name', title: t('reservations.reevaluationList.outsourcingCol'), lg: colWidths.inProgress.outsourcing_company_name[countryCode] || 2, xs: 12 },
      );
    }

    baseColumns.push(
      { key: 'last_evaluation_date', title: t('reservations.reevaluationList.lastEvaluationDateCol'), lg: colWidths.inProgress.last_evaluation_date[countryCode] || 2, xs: 12 },
    );

    if (showAgeColumn) {
      baseColumns.push(
        { key: 'days_since_issued', title: t('reservations.reevaluationList.ageCol'), lg: colWidths.inProgress.days_since_issued[countryCode] || 1, xs: 12, sortable: true },
      );
    }

    if (showExpirationColumn) {
      baseColumns.push(
        { key: 'days_to_expire', title: t('reservations.reevaluationList.expirationCol'), lg: 2, xs: 12, sortable: true },
      );
    }

    baseColumns.push(
      { key: 'prestation', title: useProtocolLabel ? t('reservations.reevaluationList.protocolCol') : t('reservations.reevaluationList.serviceCol'), lg: colWidths.inProgress.prestation[countryCode] || 2, xs: 12 },
    );

    if (showTypeColumn) {
      baseColumns.push(
        { key: 'prescription_type', title: t('reservations.reevaluationList.typeCol'), lg: 2, xs: 12 },
      );
    }

    baseColumns.push(
      { key: 'documents', title: t('reservations.reevaluationList.documentsCol'), lg: colWidths.inProgress.documents[countryCode] || 3, xs: 12 }
    );

    if (showBranchEvaluationColumn) {
      baseColumns.push({ key: 'presence_required', title: t('reservations.reevaluationList.branchEvaluationCol'), lg: 1, xs: 12 });
    }

    if (showUploadHistoryColumn) {
      baseColumns.push({ key: 'upload_history', title: (
        <span className="two-line-header">
          {t('reservations.reevaluationList.uploadHistoryCol')
            .replace('Fecha', '|Fecha')
            .split('|')
            .map((line, i) => <div key={i}>{line}</div>)}
        </span>
      ), lg: hasExtraColumns ? 2 : 4, xs: 12, sortable: true });
    }

    return baseColumns;
  }, [showOutsourcingColumn, showAgeColumn, showExpirationColumn, showTypeColumn, useProtocolLabel, hasExtraColumns, showBranchEvaluationColumn, showUploadHistoryColumn, t, countryCode]);

  const completedDocumentsColumns = useMemo(() => {
    const baseColumns = [
      {
        key: 'full_name',
        title: t('reservations.reevaluationList.firstNameCol'),
        lg: 3,
        xs: 12,
        bodyClassName: 'align-item-center p-10',
      },
      { key: 'document_number', title: t('reservations.reevaluationList.documentNumberCol'), lg: 2, xs: 12 },
      { key: 'reservation_number', title: t('reservations.reevaluationList.reservationNumberCol'), lg: 1, xs: 12 },
      { key: 'company', title: t('reservations.reevaluationList.companyCol'), lg: 3, xs: 12 },
      { key: 'last_evaluation_date', title: t('reservations.reevaluationList.lastEvaluationDateCol'), lg: 3, xs: 12 },
      { key: 'days_to_expire', title: t('reservations.reevaluationList.expirationCol'), lg: 2, xs: 12, sortable: true },
      { key: 'prestation', title: t('reservations.reevaluationList.serviceCol'), lg: 2, xs: 12 },
    ];
    if (showBranchEvaluationColumn) {
      baseColumns.push({ key: 'presence_required', title: t('reservations.reevaluationList.branchEvaluationCol'), lg: 1, xs: 12 });
    }
    
    if (showUploadHistoryColumn) {
      baseColumns.push({ key: 'upload_history', title: (
        <span className="two-line-header">
          {t('reservations.reevaluationList.uploadHistoryCol')
            .replace('Fecha', '|Fecha')
            .split('|')
            .map((line, i) => <div key={i}>{line}</div>)}
        </span>
      ), lg: 4, xs: 12, sortable: true });
    }
    return baseColumns;
  }, [showBranchEvaluationColumn, showUploadHistoryColumn, t]);

  const finalizedColumns = useMemo(() => {
    const baseColumns = [
      { key: 'full_name', title: t('reservations.reevaluationList.firstNameCol'), lg: colWidths.finalized.full_name[countryCode] || 3, xs: 12 },
      { key: 'document_number', title: t('reservations.reevaluationList.documentNumberCol'), lg: 2, xs: 12 },
      { key: 'reservation_number', title: t('reservations.reevaluationList.reservationNumberCol'), lg: colWidths.finalized.reservation_number[countryCode] || 2, xs: 12 },
      { key: 'company', title: t('reservations.reevaluationList.companyCol'), lg: colWidths.finalized.company[countryCode] || 3, xs: 12 },
    ];

    if (showOutsourcingColumn) {
      baseColumns.push(
        { key: 'outsourcing_company_name', title: t('reservations.reevaluationList.outsourcingCol'), lg: colWidths.finalized.outsourcing_company_name[countryCode] || 2, xs: 12 },
      );
    }

    baseColumns.push(
      { key: 'last_evaluation_date', title: t('reservations.reevaluationList.lastEvaluationDateCol'), lg: colWidths.finalized.last_evaluation_date[countryCode] || 3, xs: 12 },
      { key: 'prestation', title: useProtocolLabel ? t('reservations.reevaluationList.protocolCol') : t('reservations.reevaluationList.serviceCol'), lg: colWidths.finalized.prestation[countryCode] || 3, xs: 12 },
    );

    if (showTypeColumn) {
      baseColumns.push(
        { key: 'prescription_type', title: t('reservations.reevaluationList.typeCol'), lg: 2, xs: 12 },
      );
    }

    baseColumns.push(
      { key: 'documents', title: t('reservations.reevaluationList.documentsCol'), lg: colWidths.finalized.documents[countryCode] || 3, xs: 12 },
    );

    if (showBranchEvaluationColumn) {
      baseColumns.push({ key: 'presence_required', title: t('reservations.reevaluationList.branchEvaluationCol'), lg: 1, xs: 12 });
    }
    
    baseColumns.push(
      { key: 'result_text', title: t('reservations.reevaluationList.statusResultCol'), lg: colWidths.finalized.result_text[countryCode] || 3, xs: 12 },
    );

    return baseColumns;
  }, [showOutsourcingColumn, showTypeColumn, useProtocolLabel, hasExtraColumns, showBranchEvaluationColumn, t, countryCode]);

  return (
    <Row justify="center" className="patient-reevaluation-list-wrapper">
      <Col lg={24} xs={24}>
        <Row justify="center">
          <Col lg={24} xs={24} className="patient-reevaluation-list-wrapper__header pt-20 pb-10">
            <Row justify="center">
              <Col lg={22} xs={22}>
                <Row>
                  <Col>
                    <Button onClick={() => navigate(-1)} className="cmt-button primary non-outline p-0" type="primary">
                      <i className="icon-system-chevron-left" />
                      Volver
                    </Button>
                  </Col>
                  <Col lg={24} xs={24}>
                    <h1 className="text-center mb-10">{t('reservations.reevaluationList.title')}</h1>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>
          <Col lg={22} xs={22} className="patient-reevaluation-list-wrapper__body mt-30">
            <Row gutter={[0, 30]}>
              {renderSelectFilters()}
              <Col lg={24} xs={24} className="table-wrapper">
                <Row justify="center">
                  <Col lg={24} xs={24}>
                    <h2>{t('reservations.reevaluationList.patientsForReevaluation')}</h2>
                  </Col>
                  {loading ? (
                    <Spin className="cmt-spin mt-50" />
                  ) : (
                    <Col lg={24} xs={24}>
                      {countrySettings.reevaluationList.showAttendanceLegend[countryCode] && (
                      <div className="attendance-legend-wrapper">
                        <div className="attendance-legend-item">
                          <div className="attendance-legend-dot in-person" />
                          <span>{t('reservations.reevaluationList.attendanceLegendInPerson')}</span>
                        </div>
                        <div className="attendance-legend-item">
                          <div className="attendance-legend-dot remote" />
                          <span>{t('reservations.reevaluationList.attendanceLegendRemote')}</span>
                        </div>
                      </div>
                      )}
                      <Tabs defaultActiveKey="1">
                        <TabPane tab={t('reservations.reevaluationList.activeTab')} key="1">
                          <ReevaluationTable
                            columns={inProgressColumns}
                            data={inProgressReevaluations}
                            loading={advancedLoading.inProgress}
                            pagination={advancedPagination.inProgress}
                            filters={{
                              ...advancedFilters,
                              sort: {
                                key: sorting.inProgress?.orderBy,
                                order: sorting.inProgress?.orderDirection
                              }
                            }}
                            onPageChange={(direction) => handlePageChange('inProgress', direction)}
                            onOrderChange={(columnKey, direction) => handleOrderChange('inProgress', columnKey, direction)}
                            countryCode={countryCode}
                            showActions={true}
                          />
                        </TabPane>
                        <TabPane tab={t('reservations.reevaluationList.finalizedTab')} key="2">
                          <ReevaluationTable
                            columns={finalizedColumns}
                            data={finalizedReevaluations}
                            loading={advancedLoading.finalized}
                            pagination={advancedPagination.finalized}
                            filters={{
                              ...advancedFilters,
                              sort: {
                                key: sorting.finalized?.orderBy,
                                order: sorting.finalized?.orderDirection
                              }
                            }}
                            onPageChange={(direction) => handlePageChange('finalized', direction)}
                            onOrderChange={(columnKey, direction) => handleOrderChange('finalized', columnKey, direction)}
                            countryCode={countryCode}
                          />
                        </TabPane>
                      </Tabs>
                    </Col>
                  )}
                </Row>
              </Col>
            </Row>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export default PatientReevaluationList;
